/**
 * Edit a voiceover's words, or change its voice. The recording's sentences come from speech
 * recognition; in "Edit the words" mode only the sentences you change are spoken again (in the
 * recording's own cloned voice, or another) and spliced into the original take. "Change the voice"
 * speaks every sentence with the chosen voice. Either way the result is a new clip you can swap in
 * on the timeline (one undo step).
 */

import type { Asset, Subtitle } from "@studio/engine";
import { Loader2, Play, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, fileUrl, type CloneStatus, type Job, type VoiceInfo, type VoiceSegment } from "../api";
import { flush } from "./save";
import { useEditor } from "./store";

export const SAME = "same";

async function runJob(start: Promise<Job>, onProgress: (j: Job) => void): Promise<Job> {
  let job = await start;
  while (["queued", "running"].includes(job.status)) {
    onProgress(job);
    await new Promise((r) => setTimeout(r, 700));
    job = await api.job(job.id);
  }
  if (job.status !== "done") throw new Error(job.message || "failed");
  return job;
}

const stamp = (t: number) => `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, "0")}`;
const norm = (t: string) => t.toLowerCase().replace(/[^a-z0-9' ]+/g, " ").split(/\s+/).filter(Boolean).join(" ");

/** <option>s for a voice <select>: custom voices (and, with `same`, the recording's own) before the built-in ones. */
export function VoiceOptions({ voices, same, cloning = true }: { voices: VoiceInfo[]; same?: boolean; cloning?: boolean }) {
  const custom = voices.filter((v) => v.custom);
  const builtin = voices.filter((v) => !v.custom);
  const off = cloning ? "" : " (cloning service off)";
  return (
    <>
      {same && <optgroup label="This recording"><option value={SAME} disabled={!cloning}>Same voice, cloned from this recording{off}</option></optgroup>}
      {custom.length > 0 && (
        <optgroup label="Custom voices">{custom.map((v) => <option key={v.id} value={v.id} disabled={!cloning}>{v.name}{off}</option>)}</optgroup>
      )}
      <optgroup label="Built-in voices">{builtin.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}</optgroup>
    </>
  );
}

export function VoiceDialog({ assetId }: { assetId: string }) {
  const project = useEditor((s) => s.project)!;
  const src = project.assets.find((a) => a.id === assetId);
  const [orig, setOrig] = useState<VoiceSegment[] | null>(null);
  const [segs, setSegs] = useState<VoiceSegment[] | null>(null);
  const [voices, setVoices] = useState<VoiceInfo[]>([]);
  const [clone, setClone] = useState<CloneStatus | null>(null);
  const [mode, setMode] = useState<"patch" | "speak">("patch");
  const [voice, setVoice] = useState(SAME);
  const [speed, setSpeed] = useState(1);
  const [keepTiming, setKeepTiming] = useState(true);
  const [busy, setBusy] = useState<{ what: string; job?: Job } | null>({ what: "Reading the words" });
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<{ asset: Asset; shift: number; changed: number; subtitles?: Subtitle[] } | null>(null);
  const [done, setDone] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const close = () => useEditor.getState().set({ panel: null });
  const cloning = !!clone?.ok;

  useEffect(() => {
    api.voices().then(setVoices).catch(() => {});
    api.cloneStatus().then(setClone).catch((e) => setClone({ ok: false, error: e.message }));
    runJob(api.voice(project.id, assetId, { mode: "script" }), (job) => setBusy({ what: "Reading the words", job }))
      .then((j) => {
        const s = (j.output?.segments as VoiceSegment[]) ?? [];
        setOrig(s);
        setSegs(s.map((x) => ({ ...x })));
      })
      .catch((e) => setErr(e.message))
      .finally(() => setBusy(null));
  }, [project.id, assetId]);

  // without the cloning service only built-in voices can speak
  useEffect(() => {
    if (clone && !clone.ok && (voice === SAME || voice.startsWith("cv_"))) setVoice(localStorage.getItem("studio:voice") ?? "am_michael");
  }, [clone, voice]);

  const changed = useMemo(() => (orig && segs ? segs.map((s, i) => norm(s.text) !== norm(orig[i].text)) : []), [orig, segs]);
  const nChanged = changed.filter(Boolean).length;

  const pick = (v: string) => {
    setVoice(v);
    if (!v.startsWith("cv_") && v !== SAME) {
      try {
        localStorage.setItem("studio:voice", v);
      } catch {}
    }
  };
  const switchMode = (m: "patch" | "speak") => {
    setMode(m);
    setResult(null);
    setDone("");
    if (m === "patch" && cloning) setVoice(SAME);
    if (m === "speak" && voice === SAME) setVoice(localStorage.getItem("studio:voice") ?? "am_michael");
  };

  const run = async (what: "preview" | "go") => {
    if (!segs) return;
    setErr("");
    setDone("");
    const index = what === "preview" ? Math.max(0, changed.indexOf(true)) : 0;
    const label = what === "preview" ? "Speaking one sentence" : mode === "patch" ? `Re-recording ${nChanged} sentence${nChanged === 1 ? "" : "s"}` : "Speaking";
    setBusy({ what: label });
    try {
      await flush();
      const body = { mode: what === "preview" ? ("preview" as const) : mode, voice, speed, keepTiming, index, segments: segs };
      const j = await runJob(api.voice(project.id, assetId, body), (job) => setBusy({ what: label, job }));
      if (what === "preview") setPreview(`${fileUrl(project.id, j.output!.file!)}?v=${j.id}`);
      else setResult({ asset: j.output!.asset as Asset, shift: j.output?.shift ?? 0, changed: j.output?.changed?.length ?? 0, subtitles: j.output?.subtitles });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const use = (replace: boolean) => {
    if (!result) return;
    const { asset, shift, subtitles } = result;
    const n = project.audio.filter((c) => c.assetId === assetId).length;
    const retimed = mode === "speak" && !keepTiming;
    useEditor.getState().edit((d) => {
      if (!d.assets.some((a) => a.id === asset.id)) d.assets.push(asset);
      if (!replace) return;
      if (subtitles) d.subtitles = subtitles; // the captions follow the new words and timing
      for (const c of d.audio) {
        if (c.assetId !== assetId) continue;
        c.assetId = asset.id;
        if (retimed) {
          c.trimIn = 0;
          c.trimOut = null;
        } else if (shift && c.trimOut != null) {
          c.trimOut += shift; // the take got longer: keep playing to its new end
        }
      }
    });
    window.dispatchEvent(new Event("studio:poll"));
    const moved = shift ? ` Audio after the last change now plays ${shift > 0 ? "+" : ""}${shift.toFixed(2)} s later.` : "";
    const caps = replace && subtitles ? " The captions were updated to match." : "";
    setDone(replace ? `Replaced on the timeline (${n} clip${n === 1 ? "" : "s"}). Undo puts the original back.${moved}${caps}` : `Added to the project's audio.${moved}`);
  };

  const makeVoice = async () => {
    const name = prompt("Name for the new voice (it will be cloned from this recording):", src ? src.name.replace(/\.[^.]+$/, "") : "");
    if (name === null) return;
    setErr("");
    setNote("");
    setBusy({ what: "Making the voice" });
    try {
      const j = await runJob(api.createVoiceFromAsset(project.id, assetId, name), (job) => setBusy({ what: "Making the voice", job }));
      setNote(j.output?.warning ?? `Voice “${j.output?.voice?.name}” is ready; it's in the list and on the Voices page.`);
      api.voices().then(setVoices).catch(() => {});
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const edit = (i: number, text: string) => setSegs((s) => s!.map((x, j) => (j === i ? { ...x, text } : x)));
  const hasCues = project.subtitles.length > 0;
  const canGo = !!segs?.length && (mode === "speak" || nChanged > 0);

  return (
    <div className="modal-back" onPointerDown={close}>
      <div className="modal voice" onPointerDown={(e) => e.stopPropagation()}>
        <header>
          <h3>{mode === "patch" ? "Edit the words" : "Change voice"}: {src?.name}</h3>
          <button className="icon" onClick={close}><X size={18} /></button>
        </header>
        <div className="voice-modes">
          <label><input type="radio" checked={mode === "patch"} onChange={() => switchMode("patch")} /> Edit the words: re-record only the sentences I change</label>
          <label><input type="radio" checked={mode === "speak"} onChange={() => switchMode("speak")} /> Change the voice of the whole recording</label>
        </div>
        <p className="muted small">
          {mode === "patch"
            ? "Change any words below. Only sentences whose words differ are spoken again, in the voice you pick, and spliced into the original recording where its pauses are; everything else is untouched. A changed sentence starts where it did; if it runs longer than before, what follows moves later."
            : "The words below come from the recording; fix anything misheard before generating. Each sentence is spoken with the voice you pick, on this server."}
          {clone && !clone.ok && <> <span className="bad">{clone.error ?? "The voice cloning service is off"}</span> — only built-in voices can speak until it is started (<code>docker compose --profile clone up -d</code>).</>}
        </p>
        <div className="voice-controls">
          <label>Voice
            <select value={voice} onChange={(e) => pick(e.target.value)}>
              <VoiceOptions voices={voices} same cloning={cloning} />
            </select>
          </label>
          <label>Speed
            <input type="number" className="num short" min={0.6} max={1.6} step={0.05} value={speed} onChange={(e) => setSpeed(+e.target.value || 1)} />
          </label>
          {mode === "speak" && (
            <label className="check" title="Each sentence starts where it did in the original, so scenes timed to this clip still line up">
              <input type="checkbox" checked={keepTiming} onChange={(e) => setKeepTiming(e.target.checked)} /> Keep the original timing
            </label>
          )}
          <button disabled={!!busy || !cloning} onClick={() => void makeVoice()} title="Save this recording's voice as a custom voice for other voiceovers">Make a voice from this recording</button>
          <span className="grow" />
          <button disabled={!!busy || !canGo} onClick={() => run("preview")} title={mode === "patch" ? "Hear the first changed sentence" : "Hear the first sentence"}>
            <Play size={14} /> Preview {mode === "patch" ? "a changed sentence" : "first sentence"}
          </button>
          <button className="primary" disabled={!!busy || !canGo} onClick={() => run("go")}>
            {mode === "patch" ? `Re-record ${nChanged || ""} changed sentence${nChanged === 1 ? "" : "s"}` : "Generate"}
          </button>
        </div>
        {busy && (
          <p className="muted small"><Loader2 size={13} className="spin" /> {busy.job?.message && busy.job.message !== "starting" ? busy.job.message : busy.what}
            {busy.job?.status === "queued" ? " (waiting for the worker)" : busy.job ? ` ${Math.round(busy.job.progress * 100)}%` : "…"}</p>
        )}
        {err && <p className="bad small">{err}</p>}
        {note && <p className="good small">{note}</p>}
        <div className="voice-compare">
          {src && <label>Original <audio controls preload="none" src={fileUrl(project.id, src.file)} /></label>}
          {preview && <label>Preview <audio controls autoPlay src={preview} /></label>}
          {result && <label>New: {result.asset.name}{result.changed ? ` — ${result.changed} sentence${result.changed === 1 ? "" : "s"} re-recorded` : ""} <audio controls src={fileUrl(project.id, result.asset.file)} /></label>}
        </div>
        {result && (
          <div className="analysis-actions" style={{ justifyContent: "flex-start" }}>
            <button className="primary" onClick={() => use(true)} disabled={!!done}>Replace on the timeline</button>
            <button onClick={() => use(false)} disabled={!!done}>Just add to the project</button>
            {done && <span className="good small">{done}{hasCues && !result?.subtitles ? " Regenerate captions to match the new words." : ""}</span>}
          </div>
        )}
        {segs && (
          <div className="import-table voice-script">
            <table>
              <thead><tr><th>At</th><th>Words{mode === "patch" && nChanged > 0 ? ` (${nChanged} changed)` : ""}</th></tr></thead>
              <tbody>
                {segs.map((s, i) => (
                  <tr key={i} className={mode === "patch" && changed[i] ? "changed" : ""}>
                    <td className="muted">{stamp(s.start)}</td>
                    <td><textarea rows={2} value={s.text} onChange={(e) => edit(i, e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
